import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, Gift, X } from 'lucide-react';

import { useUser } from '@/modules/auth/hooks/use-user';
import { savePendingCoupon } from '@/modules/orders/utils/pending-coupon';

import { claimHomeScreenReward, getHomeScreenOffer } from '../api';
import { isStandalone } from '../push-client';
import type { HomeScreenGranted } from '../types';
import { usePushInvite, usePushSync } from '../use-push-invite';
import { isReturnVisit, markHomeScreenRewardClaimed, wasHomeScreenRewardClaimed } from '../utils';
import { AndroidInstallButton } from './android-install-button';
import { IosHomeScreenSteps } from './ios-home-screen-steps';
import { PushGrantedNote } from './push-granted-note';

type Props = {
  slug: string;
  storeName?: string;
  merchantId?: string | null;
};

/**
 * PRAT-165 / PRAT-43 — na página da loja:
 * - convida a ativar as ofertas da loja só quando o cliente VOLTA à loja (nunca
 *   na 1ª visita) e não disse "Agora não" nos últimos 7 dias;
 * - no iPhone fora da tela inicial, ensina a adicionar em 3 passos e mostra o brinde;
 * - aberto pela tela inicial e com login, entrega o brinde (uma vez).
 */
export function StorePushPrompt({ slug, storeName, merchantId }: Props) {
  const { user } = useUser();
  const userId = user.get()?.id ?? null;
  const [returning] = useState(() => isReturnVisit(slug));
  const [granted, setGranted] = useState<HomeScreenGranted[]>([]);
  usePushSync(slug, merchantId);
  const invite = usePushInvite({
    slug,
    merchantId,
    channel: 'loja',
    visible: returning && !!slug,
  });
  const promptVisible = returning && invite.canAsk && !invite.snoozed;
  const state = invite.state;

  const offerQuery = useQuery({
    queryKey: ['push-home-screen-offer', slug],
    queryFn: () => getHomeScreenOffer(slug),
    enabled: !!slug && state === 'needs-home-screen' && promptVisible,
    staleTime: 5 * 60 * 1000,
  });

  // Aberto pela tela inicial e logado: brinde (uma vez por loja e cliente).
  useEffect(() => {
    if (!slug || !userId || !isStandalone() || wasHomeScreenRewardClaimed(slug, userId)) return;
    let alive = true;
    claimHomeScreenReward(slug)
      .then(list => {
        markHomeScreenRewardClaimed(slug, userId);
        // O cupom já vai para o carrinho: o checkout aplica o cupom pendente sozinho.
        const usable = list.find(r => r.new) ?? list.find(r => !r.used);
        if (usable) savePendingCoupon(usable.code);
        const fresh = list.filter(r => r.new);
        if (alive && fresh.length) setGranted(fresh);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [slug, userId]);

  const rewardCard =
    granted.length > 0 ? (
      <div
        className="mt-3 rounded-2xl border border-primary/30 bg-primary/5 p-4"
        data-testid="push-reward-granted"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3">
            <Gift className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div>
              <p className="text-[15px] font-bold text-gray-900">Seu presente chegou</p>
              {granted.map(g => (
                <p key={g.code} className="mt-1 text-[13px] text-gray-700">
                  {g.text}. O cupom{' '}
                  <span className="font-bold tracking-wide text-gray-900">{g.code}</span> é só seu e
                  já entra no carrinho.
                </p>
              ))}
            </div>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            className="-m-2 p-2 text-gray-400"
            onClick={() => setGranted([])}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    ) : null;

  if (invite.granted !== undefined) {
    return (
      <>
        <div className="mt-3">
          <PushGrantedNote
            granted={invite.granted}
            onClose={invite.dismissGranted}
            message={`Você vai receber as ofertas de ${storeName || 'a loja'}.`}
          />
        </div>
        {rewardCard}
      </>
    );
  }

  if (!promptVisible) return rewardCard;

  if (state === 'needs-home-screen') {
    const rewards = offerQuery.data ?? [];
    return (
      <>
        <div
          className="mt-3 rounded-2xl border border-gray-200 bg-white p-4"
          data-testid="push-home-screen-guide"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[15px] font-bold text-gray-900">
                Ofertas e cupons {storeName ? `de ${storeName}` : 'da loja'} no iPhone
              </p>
              <p className="mt-0.5 text-[13px] text-gray-600">
                No iPhone, as notificações chegam com o app na tela de início:
              </p>
            </div>
            <button
              type="button"
              aria-label="Agora não"
              className="-m-2 p-2 text-gray-400"
              onClick={invite.snooze}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <IosHomeScreenSteps />
          {rewards.length > 0 && (
            <div
              className="mt-3 flex gap-2 rounded-xl bg-primary/5 p-3"
              data-testid="push-home-screen-reward"
            >
              <Gift className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <p className="text-[13px] text-gray-800">
                <b>Ganhe um presente:</b> {rewards.map(r => r.text).join(' + ')} quando abrir pela
                tela de início
                {userId ? '' : ' (com login)'}.
              </p>
            </div>
          )}
        </div>
        {rewardCard}
      </>
    );
  }

  return (
    <>
      <div
        className="mt-3 flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4"
        data-testid="push-opt-in"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bell className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-gray-900">Ofertas e cupons só pra quem ativa</p>
          <p className="text-[13px] text-gray-600">
            Ative os avisos e receba as promoções {storeName ? `de ${storeName}` : 'da loja'} no
            celular.
          </p>
          {invite.offer && (
            <p
              className="mt-1 flex items-center gap-1 text-[13px] font-semibold text-primary"
              data-testid="push-optin-offer"
            >
              <Gift className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Tem cupom te esperando: {invite.offer.text}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={invite.busy}
              onClick={invite.activate}
              className="h-11 rounded-xl bg-primary px-4 text-[14px] font-semibold text-primary-foreground disabled:opacity-60"
              data-testid="push-activate"
            >
              {invite.busy ? 'Ativando...' : 'Ativar'}
            </button>
            <button
              type="button"
              onClick={invite.snooze}
              className="h-11 rounded-xl px-3 text-[14px] text-gray-600"
            >
              Agora não
            </button>
          </div>
          <AndroidInstallButton className="mt-2" />
        </div>
      </div>
      {rewardCard}
    </>
  );
}
